import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiCheck, FiStar } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase'; // Make sure this import exists
import EmbeddedCheckoutModal from '../../components/EmbeddedCheckout';

// Stripe price ID for the monthly subscription
const MONTHLY_PRICE_ID = 'price_1QqosGGgbRrkife1OlM6u9Oe';
const ANNUAL_PRICE_ID = 'price_1QqosGGgbRrkife12don62as'; // Replace with your actual annual price ID
const LIFETIME_PRICE_ID = 'price_1QqosGGgbRrkife1t3e6neXG'; // Replace with your actual lifetime price ID

const getTimeRemaining = () => {
  const endDate = new Date('2025-02-28T23:59:59');
  const now = new Date();
  const total = endDate.getTime() - now.getTime();
  
  const days = Math.floor(total / (1000 * 60 * 60 * 24));
  const hours = Math.floor((total % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((total % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((total % (1000 * 60)) / 1000);
  
  return { total, days, hours, minutes, seconds };
};

export default function SubscribePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [userId, setUserId] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(getTimeRemaining());
  const [showCheckoutModal, setShowCheckoutModal] = useState<string | null>(null);

  useEffect(() => {
    async function getUserId() {
      const { data: { user }, error } = await supabase.auth.getUser();
      
      if (error) {
        console.error('Error fetching user:', error);
        navigate('/login'); // Redirect to login if there's an error
        return;
      }

      if (!user) {
        navigate('/login'); // Redirect to login if no user
        return;
      }

      setUserId(user.id);
    }

    getUserId();
  }, [navigate]);

  useEffect(() => {
    const timer = setInterval(() => {
      const remaining = getTimeRemaining();
      setTimeRemaining(remaining);
      
      if (remaining.total <= 0) {
        clearInterval(timer);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const premiumFeatures = [
    {
      id: 'unlimited',
      description: t('payment.subscription.features.unlimited')
    },
    {
      id: 'support',
      description: t('payment.subscription.features.support')
    },
    {
      id: 'updates',
      description: t('payment.subscription.features.updates')
    },
    /*
    {
      id: 'mentoring',
      description: t('payment.subscription.features.mentoring')
    }
    */
  ];

  const freeFeatures = [
    {
      id: 'oneProject',
      description: t('subscribe.features.oneProject')
    },
    {
      id: 'basicFeatures',
      description: t('subscribe.features.basicFeatures')
    }
  ];

  const plans = [
    {
      id: 'free',
      title: t('subscribe.freePlan'),
      description: t('subscribe.freePlanDesc'),
      price: '0',
      period: '',
      features: freeFeatures,
      action: () => navigate('/register'),
      actionText: t('subscribe.freePlan'),
      buttonClass: 'btn-outline',
      badge: null
    },
    {
      id: 'monthly',
      title: t('subscribe.monthlyPlan'),
      description: t('subscribe.monthlyPlanDesc'),
      price: '9.99',
      period: '/mo',
      features: premiumFeatures,
      priceId: MONTHLY_PRICE_ID,
      buttonClass: 'btn-primary',
      badge: null
    },
    {
      id: 'annual',
      title: t('subscribe.annualPlan'),
      description: t('subscribe.annualPlanDesc'),
      price: '99',
      period: '/ano',
      features: premiumFeatures,
      priceId: ANNUAL_PRICE_ID,
      buttonClass: 'btn-primary',
      badge: `2 ${t('subscribe.monthsFree')}`,
      savings: t('subscribe.saveWithAnnual')
    },
    {
      id: 'lifetime',
      title: t('subscribe.lifetimePlan'),
      description: t('subscribe.lifetimePlanDesc'),
      price: '150',
      period: t('subscribe.oneTime'),
      features: premiumFeatures,
      priceId: LIFETIME_PRICE_ID,
      buttonClass: 'btn-primary',
      badge: t('subscribe.bestValue'),
      savings: t('subscribe.lifetimeValue')
    }
  ];

  // Early return if no userId is available
  if (!userId) {
    return null; // Or a loading spinner
  }

  return (
    <div className="min-h-screen py-12 px-2 sm:px-6 lg:px-8 bg-base-200">
      <div className="max-w-5xl mx-auto">
        <div className="alert alert-warning shadow-lg mb-8 p-2 sm:p-4 max-w-2xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-center w-full gap-2">
            <div className="flex items-center gap-2 shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
              <span className="font-bold text-sm sm:text-base whitespace-nowrap">{t('subscribe.limitedOffer')}</span>
            </div>
            <div className="grid grid-cols-4 gap-1 sm:gap-4 text-center w-full max-w-xs sm:max-w-md">
              <div className="flex flex-col p-1 sm:p-3 bg-neutral rounded-box text-neutral-content">
                <span className="countdown font-mono text-lg sm:text-2xl">
                  <span style={{ "--value": timeRemaining.days } as any}></span>
                </span>
                <span className="text-xs sm:text-sm">{t('subscribe.days')}</span>
              </div> 
              <div className="flex flex-col p-1 sm:p-3 bg-neutral rounded-box text-neutral-content">
                <span className="countdown font-mono text-lg sm:text-2xl">
                  <span style={{ "--value": timeRemaining.hours } as any}></span>
                </span>
                <span className="text-xs sm:text-sm">{t('subscribe.hours')}</span>
              </div> 
              <div className="flex flex-col p-1 sm:p-3 bg-neutral rounded-box text-neutral-content">
                <span className="countdown font-mono text-lg sm:text-2xl">
                  <span style={{ "--value": timeRemaining.minutes } as any}></span>
                </span>
                <span className="text-xs sm:text-sm">{t('subscribe.minutes')}</span>
              </div>
              <div className="flex flex-col p-1 sm:p-3 bg-neutral rounded-box text-neutral-content">
                <span className="countdown font-mono text-lg sm:text-2xl">
                  <span style={{ "--value": timeRemaining.seconds } as any}></span>
                </span>
                <span className="text-xs sm:text-sm">{t('subscribe.seconds')}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center mb-12">
          <h1 className="text-4xl font-extrabold text-primary">
            {t('subscribe.title')}
          </h1>
          <p className="mt-4 text-xl text-base-content">
            {t('subscribe.description')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan) => (
            <div key={plan.id} className="border border-primary rounded-lg shadow-sm divide-y divide-base-300 bg-base-100 relative">
              {plan.badge && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="badge badge-secondary">{plan.badge}</span>
                </div>
              )}
              <div className="p-6">
                <h2 className="text-2xl font-semibold text-base-content flex items-center gap-2">
                  {plan.title}
                  {plan.id !== 'free' && <FiStar className="text-warning" />}
                </h2>
                <p className="mt-4 text-base-content/70">
                  {plan.description}
                </p>
                <p className="mt-8">
                  <span className="text-4xl font-extrabold text-base-content">
                    R${plan.price}
                  </span>
                  {plan.period && (
                    <span className="text-base font-medium text-base-content">
                      {plan.period}
                    </span>
                  )}
                </p>
                {plan.savings && (
                  <p className="mt-2 text-sm text-success">
                    {plan.savings}
                  </p>
                )}
                {plan.id === 'free' ? (
                  <button
                    className={`mt-8 block w-full btn ${plan.buttonClass}`}
                    onClick={plan.action}
                  >
                    {plan.actionText}
                  </button>
                ) : (
                  <EmbeddedCheckoutModal 
                    priceId={plan.priceId}
                    userId={userId}
                    onSuccess={() => navigate('/learn')}
                    onCancel={() => setShowCheckoutModal(null)}
                  />
                )}
              </div>
              <div className="pt-6 pb-8 px-6">
                <h3 className="text-xs font-semibold text-base-content uppercase tracking-wide">
                  {t('subscribe.whatsIncluded')}
                </h3>
                <ul className="mt-6 space-y-4">
                  {plan.features.map((feature) => (
                    <li key={feature.id} className="flex space-x-3">
                      <FiCheck className="flex-shrink-0 h-5 w-5 text-success" />
                      <span className="text-base-content/70">
                        {feature.description}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <h3 className="text-lg font-medium text-base-content">
            {t('subscribe.needOtherPayment')}
          </h3>
          <div className="mt-2">
            <a 
              href="/community" 
              className="link link-primary font-medium"
            >
              {t('subscribe.joinDiscord')} →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}